/**
 * Created by rathompson on 2/8/22
 * File: InProgressInstances
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { BUTTON_ACTIVATING } from '../../../../constants/LabelConstants'
import OnboardingCard from '../OnboardingCard'
import LoadingButton from '../../../../../common/components/LoadingButton'
import { noop } from '@panwds/react-ui'
import { PROVISIONING_STATUSES } from '../../../../constants/constants'
import InstanceCard from './InstanceCard'
import { launchAppInstance } from '../../../../../common/utils/Sparky'
import { ARRAY } from '../../../../../common/utils/Utils'
import { ENTERPRISE } from '../../../constants/SubscriptionConstant'
import SubscriptionService from '../../../../services/SubscriptionService'
import { LICENSE_CONTEXT_TO_APP_ID, APP_ID_FOR_TITLE } from '../../../../../common/constants/GlobalConstants'
import AppContext from '../../../../../common/context/AppContext'
import InstanceTitle from './InstanceTitle'

const InProgressInstances = ({ hash, licenseContext, licenseInstances, tsg_id, sgKeyBy }) => {
  const [licenses, setLicenses] = useState(null)
  const [loading, setLoading] = useState(false)
  const { apps } = useContext(AppContext)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await SubscriptionService.listLicense.get({
        tsg_id,
        hash,
        flow_type: ENTERPRISE,
        license_context: licenseContext
      })
      const licenses = response?.['de_hash']?.data.licenses ?? response.data[0]?.licenses ?? []
      setLicenses(licenses)
    }
    catch (error) {
      // setError(error.message)
    }
    setLoading(false)
  }, [hash, licenseContext, tsg_id])

  const computeCompletionPercent = useCallback(() => {
    const instances = licenseInstances.filter(instance => instance?.provisioning_status !== PROVISIONING_STATUSES.ERROR)
    const getNumForStatus = (status) => {
      switch (status) {
        case PROVISIONING_STATUSES.INITIALIZING:
          return 3
        case PROVISIONING_STATUSES.PROVISIONING:
          return 2
        case PROVISIONING_STATUSES.COMPLETE:
          return 1
        case PROVISIONING_STATUSES.ERROR:
        default:
          return 0
      }
    }
    if (ARRAY.hasArray(instances)) {
      let totalPercent = 0
      for (const instance of instances) {
        const instancePercent = 100 / instances?.length
        const denominator = getNumForStatus(instance.provisioning_status)
        if (denominator > 0) {
          const currentPercent = instancePercent / denominator
          totalPercent += currentPercent
        }
      }
      return Math.ceil(totalPercent)
    }
    return 0
  }, [licenseInstances])

  const percent = computeCompletionPercent()

  const primaryAppId = useMemo(() => {
    return APP_ID_FOR_TITLE[licenseInstances[0]?.app_id] || 'prisma_access'
  }, [licenseInstances])

  const appDef = useMemo(() => {
    return apps[primaryAppId] || apps[LICENSE_CONTEXT_TO_APP_ID[primaryAppId]]
  }, [apps, primaryAppId])

  const inProgressInstances = useMemo(() => {
    return licenseInstances?.map((instance, index) => {
      return <InstanceCard
        key={index}
        showButton={instance.status === 'running' && instance.provisioning_status === PROVISIONING_STATUSES.COMPLETE}
        onLaunchClick={launchAppInstance.bind(undefined, instance, licenseInstances)}
        licenses={licenses}
        {...{ instance, licenseInstances, tsg_id, loading }}
      />
    })
  }, [licenses, licenseInstances, loading, tsg_id])

  useEffect(() => {
    (() => loadData())()
  }, [hash, loadData, tsg_id])

  const hasError = licenseInstances.every(instance => instance?.provisioning_status === PROVISIONING_STATUSES.ERROR)

  return (
    <OnboardingCard appearance='activated'>
      {/* Right Controls */}
      <div className="tw-absolute tw-flex tw-items-center tw-gap-2 tw-right-3 tw-top-3">
        <div className='tw-flex tw-flex-col tw-gap-2'>
          {/*<div className="tw-flex tw-items-baseline tw-gap-2">*/}
          {/*  <Body>Activated Tenant:</Body>*/}
          {/*  <Bold>{sgKeyBy[licenseInstances[0]?.tsg_id]?.display_name}</Bold>*/}
          {/*</div>*/}
          {!hasError && (
            <LoadingButton
              addClassName='tw-self-end'
              error={hasError}
              progress={percent}
              onClick={() => null}>{BUTTON_ACTIVATING}</LoadingButton>
          )}
        </div>
      </div>
      <InstanceTitle {...{ appDef, license: licenseInstances[0] }} />
      {inProgressInstances}
    </OnboardingCard>
  )
}

InProgressInstances.propTypes = {}

InProgressInstances.defaultProps = {
  hash: '',
  licenseContext: '',
  licenseInstances: [],
  tsg_id: '',
  onLaunchClick: noop,
  sgKeyBy: {}
}
export default InProgressInstances
